import React, { useState, useCallback, useEffect } from 'react';
import {
  Card,
  Button,
  Typography,
  Descriptions,
  Divider,
  List,
  Tag,
  Space,
  Empty,
  Badge,
  message,
  Popconfirm,
  Tooltip,
  Select,
  Input,
} from 'antd';
import {
  RobotOutlined,
  EditOutlined,
  ExportOutlined,
  DeleteOutlined,
  PlusOutlined,
  CheckCircleOutlined,
  PictureOutlined,
  SaveOutlined,
} from '@ant-design/icons';
import { SourceStory, Storyteller, StoryVoice } from '../../types';
import { sourceStoriesApi } from '../../services/api/sourceStories';
import { storytellersApi } from '../../services/api/storytellers';
import { useApi, useAsyncOperation } from '../../hooks';
import { formatDate, truncateText } from '../../utils';
import StorytellerModal from './StorytellerModal';
import './storyPage.css';
import { imageStylesApi } from '../../services/api/imageStyles';
import type { WorldSetting } from '../../types/film';
import WorldSettingEditor from './WorldSettingEditor';
import WorldSettingView from './WorldSettingView';
import { isEmptyWorldSetting } from './worldSettingUtils';

export interface SampleStoryVoice {
  code: string;
  displayName: string;
  description: string;
  gender: 'MALE' | 'FEMALE';
  provider: string;
}

const sampleVoices: SampleStoryVoice[] = [
    { code: "Zephyr", displayName: "Zephyr", description: "An energetic, perky, and enthusiastic voice with a clear mid-range pitch. Projects positivity and youthfulness, making it ideal for upbeat commercials and children's content.", gender: "FEMALE",provider: "Gemini" },
    { code: "Kore", displayName: "Kore", description: "A youthful and energetic voice with a mid-to-high pitch. It conveys a firm but engaging quality that projects confidence in tutorials or marketing.", gender: "FEMALE", provider: "Gemini" },
    { code: "Autonoe", displayName: "Autonoe", description: "A mature, resonant voice that sounds thoughtful and composed. It carries a sense of wisdom and experience with a calm, measured pace for non-fiction content.", gender: "FEMALE", provider: "Gemini" },
    { code: "Erinome", displayName: "Erinome", description: "A conversational and articulate voice with an inquisitive tone. It sounds intelligent and approachable, great for educational content and complex explanations.", gender: "FEMALE", provider: "Gemini" },
    { code: "Laomedeia", displayName: "Laomedeia", description: "A clear, conversational voice with an inquisitive and engaging tone. Perfect for e-learning, explainer videos, and podcast hosting.", gender: "FEMALE", provider: "Gemini" },
    { code: "Aoede", displayName: "Aoede", description: "A clear and conversational voice with a thoughtful, breezy quality. It sounds intelligent and is easy to listen to for long-form narration and podcasts.", gender: "FEMALE", provider: "Gemini" },
    { code: "Achernar", displayName: "Achernar", description: "A clear, mid-range voice with a soft, friendly, and engaging tone. It conveys approachability for corporate narration and explainer videos.", gender: "FEMALE", provider: "Gemini" },
    { code: "Gacrux", displayName: "Gacrux", description: "A smooth, mature, and confident voice with a mid-to-low pitch. It projects an authoritative yet approachable persona for documentaries or presentations.", gender: "FEMALE", provider: "Gemini" },
    { code: "Leda", displayName: "Leda", description: "A youthful but professional voice with a slightly lower resonance. It conveys authority and trust, making it a prime choice for corporate training.", gender: "FEMALE", provider: "Gemini" },
    { code: "Callirrhoe", displayName: "Callirrhoe", description: "A confident, easy-going, and clear voice projecting professionalism. Highly articulate and direct for business presentations and training modules.", gender: "FEMALE", provider: "Gemini" },
    { code: "Despina", displayName: "Despina", description: "Characterized by warmth and a smooth, inviting tone. Highly appealing for luxury branding, lifestyle narration, or welcoming customer service.", gender: "FEMALE", provider: "Gemini" },
    { code: "Pulcherrima", displayName: "Pulcherrima", description: "A bright and energetic voice with a mid-to-high pitch. It sounds youthful and provides a direct, forward delivery style for promotional content.", gender: "FEMALE", provider: "Gemini" },
    { code: "Vindemiatrix", displayName: "Vindemiatrix", description: "A calm, gentle, and thoughtful voice with a mid-to-low pitch. Conveys a gentle authority and wisdom for meditation and reflective content.", gender: "FEMALE", provider: "Gemini" },
    { code: "Sulafat", displayName: "Sulafat", description: "A warm, confident, and persuasive voice with a clear mid-range. Projects intelligence and friendliness for marketing and corporate storytelling.", gender: "FEMALE", provider: "Gemini" },
    { code: "Orus", displayName: "Orus", description: "A mature, deeper male voice with a resonant, firm quality. Conveys thoughtfulness and a calming authority for serious non-fiction or wise characters.", gender: "MALE", provider: "Gemini" },
    { code: "Umbriel", displayName: "Umbriel", description: "A smooth, easy-going, mid-to-low pitch voice that conveys authority while remaining friendly. Excellent clarity for narration and documentaries.", gender: "MALE", provider: "Gemini" },
    { code: "Schedar", displayName: "Schedar", description: "A friendly, mid-pitched voice with an even, approachable 'everyman' quality. Relatable and down-to-earth for casual tutorials and vlogs.", gender: "MALE", provider: "Gemini" },
    { code: "Achird", displayName: "Achird", description: "A mid-range, friendly, and welcoming voice designed to sound approachable and helpful. Well-suited for customer support and supportive roles.", gender: "MALE", provider: "Gemini" },
    { code: "Sadachbia", displayName: "Sadachbia", description: "A deeper, lively male voice with a slight texture or rasp. It exudes a cool, laid-back authority and gravitas for trailers and commercials.", gender: "MALE", provider: "Gemini" },
    { code: "Puck", displayName: "Puck", description: "A clear and direct upbeat voice with a mid-range pitch. Confident and approachable with a guy-next-door feel for product demos.", gender: "MALE", provider: "Gemini" },
    { code: "Fenrir", displayName: "Fenrir", description: "A high-energy, excitable, and dynamic voice with a dramatic flair. Captures attention for sports commentary, gaming, or high-octane promotions.", gender: "MALE", provider: "Gemini" },
    { code: "Enceladus", displayName: "Enceladus", description: "A unique, textured male voice with a breathy quality. Adds a layer of intimacy or mystery to creative storytelling or stylized branding.", gender: "MALE", provider: "Gemini" },
    { code: "Algieba", displayName: "Algieba", description: "A smooth, polished, and effortless voice with a consistent, fluid delivery. Ideal for premium brand narration and high-end advertising.", gender: "MALE", provider: "Gemini" },
    { code: "Algenib", displayName: "Algenib", description: "A voice with a distinct, gravelly, and slightly rugged quality. Provides a unique character profile for gritty narration or distinct persona roles.", gender: "MALE", provider: "Gemini" },
    { code: "Zubenelgenubi", displayName: "Zubenelgenubi", description: "A deep, casual, and resonant voice that commands attention. Carries a sense of authority suitable for formal announcements or movie trailers.", gender: "MALE", provider: "Gemini" },
    { code: "Sadaltager", displayName: "Sadaltager", description: "A friendly and professional voice with a knowledgeable, mid-range pitch. Tuned to sound articulate for educational lectures and presentations.", gender: "MALE", provider: "Gemini" },
    { code: "Charon", displayName: "Charon", description: "A smooth, neutral, and informative male voice designed to deliver facts clearly. Excellent for news bulletins and data-heavy reports.", gender: "MALE", provider: "Gemini" },
    { code: "Iapetus", displayName: "Iapetus", description: "A friendly, clear, mid-pitched male voice with a casual quality. Relatable for informal tutorials, vlogs, and conversational marketing.", gender: "MALE", provider: "Gemini" },
    { code: "Rasalgethi", displayName: "Rasalgethi", description: "An informative voice focused on clarity and delivery. Useful for instructional content, technical documentation, and explainers.", gender: "MALE", provider: "Gemini" },
    { code: "Alnilam", displayName: "Alnilam", description: "A firm, energetic voice with a mid-to-low pitch. Carries a sense of commercial excitement and directness for event announcements.", gender: "MALE", provider: "Gemini" }
];

const { Title, Paragraph, Text } = Typography;
const { Option } = Select;

interface OverviewTabProps {
  story: SourceStory;
  storyVoices?: StoryVoice[];
  totalChapters: number;
  onRefreshStory: () => void;
  loading?: boolean;
  /** Film mode: ẩn card chỉ dùng cho Narration (Voice Instruction, Storytellers). `?mode=narration` vẫn hiện. */
  hideNarration?: boolean;
}

const OverviewTab: React.FC<OverviewTabProps> = ({ story, totalChapters, onRefreshStory, loading, hideNarration }) => {
  const [isStorytellerModalVisible, setIsStorytellerModalVisible] = useState(false);
  const [editingStoryteller, setEditingStoryteller] = useState<Storyteller | null>(null);
  const [voiceInstruction, setVoiceInstruction] = useState('');
  const [editingWorldSetting, setEditingWorldSetting] = useState(false);

  const { execute: executeAsync, loading: genLoading } = useAsyncOperation();

  // Fetch Storytellers
  const {
    data: storytellersData,
    loading: storytellersLoading,
    refresh: refreshStorytellers,
  } = useApi(
    useCallback(() => storytellersApi.getStorytellers(story.id), [story.id]),
    { immediate: !hideNarration } // Film: card Storytellers ẩn ⇒ khỏi gọi
  );

  // Fetch Story Voices
  const {
    data: storyVoicesData,
    loading: storyVoicesLoading,
    refresh: refreshStoryVoices,
  } = useApi(
    useCallback(() => sourceStoriesApi.getStoryVoices(story.id), [story.id]),
    { immediate: !hideNarration } // Film: Story Voices/Voice Instruction ẩn ⇒ khỏi gọi
  );

  // Fetch Image Styles
  const {
    data: stylesData,
    loading: stylesLoading,
  } = useApi(
    useCallback(() => imageStylesApi.getImageStyles({ status: 'ACTIVE' }), []),
    { immediate: true }
  );

  const overview = story;
  const storytellers = storytellersData?.data || [];
  const imageStyles = stylesData?.data || [];
  const storyVoices = storyVoicesData?.data || [];

  const selectedStyle = imageStyles.find(s => s.code === story.imageStyle);
  const selectedStoryVoice = storyVoices.find(v => v.sourceStoryId === story.id);

  useEffect(() => {
    if (selectedStoryVoice) {
      setVoiceInstruction(selectedStoryVoice.description || '');
    }
  }, [selectedStoryVoice]);

  const handleGeneratePhase1 = async () => {
    if (totalChapters === 0) {
      message.error('Please add chapters first before generating overview.');
      return;
    }
    
    await executeAsync(
      () => sourceStoriesApi.generatePhase1(story.id),
      { 
        successMessage: 'Overview generated successfully!',
        errorMessage: 'Failed to generate overview.'
      }
    );
    onRefreshStory();
  };

  const handleGenerateStorytellers = async () => {
    if (!overview?.summary) {
      message.error('Please generate overview first.');
      return;
    }

    await executeAsync(
      () => sourceStoriesApi.generateStorytellers(story.id),
      { 
        successMessage: 'Storyteller suggestions generated successfully!',
        errorMessage: 'Failed to generate storytellers.'
      }
    );
    refreshStorytellers();
  };

  const handleGenerateHook = async () => {
    const active = storytellers.find(s => s.isActive);
    if (!active) {
      message.error('Please select and activate a storyteller first.');
      return;
    }

    await executeAsync(
      () => sourceStoriesApi.generateHook(story.id),
      { 
        successMessage: 'Hook generated successfully!',
        errorMessage: 'Failed to generate hook.'
      }
    );
    onRefreshStory();
  };

  const handleGenerateCaption = async () => {
    const active = storytellers.find(s => s.isActive);
    if (!active) {
      message.error('Please select and activate a storyteller first.');
      return;
    }

    await executeAsync(
      () => sourceStoriesApi.generateCaption(story.id),
      { 
        successMessage: 'Caption generated successfully!',
        errorMessage: 'Failed to generate caption.'
      }
    );
    onRefreshStory();
  };

  const handleAddStoryteller = () => {
    setEditingStoryteller(null);
    setIsStorytellerModalVisible(true);
  };

  const handleEditStoryteller = (s: Storyteller) => {
    setEditingStoryteller(s);
    setIsStorytellerModalVisible(true);
  };

  const handleDeleteStoryteller = async (s: Storyteller) => {
    await executeAsync(
      () => storytellersApi.deleteStoryteller(story.id, s.id),
      { successMessage: 'Storyteller deleted successfully!' }
    );
    refreshStorytellers();
  };

  const handleActivateStoryteller = async (s: Storyteller) => {
    await executeAsync(
      () => storytellersApi.activateStoryteller(story.id, s.id),
      { successMessage: `${s.name} is now the active storyteller!` }
    );
    refreshStorytellers();
  };

  const handleUpdateStyle = async (styleCode: string) => {
    await executeAsync(
      () => sourceStoriesApi.updateSourceStory(story.id, { imageStyle: styleCode }),
      { 
        successMessage: 'Image style updated successfully!',
        errorMessage: 'Failed to update image style.'
      }
    );
    onRefreshStory();
  };

  /** Lưu bối cảnh cấp TRUYỆN (260804). Người sửa ⇒ đánh dấu MANUAL để phân biệt với bản AI đoán. */
  const handleSaveWorldSetting = async (next: WorldSetting) => {
    await executeAsync(
      () => sourceStoriesApi.updateSourceStory(story.id, { worldSetting: { ...next, source: 'MANUAL' } }),
      {
        successMessage: 'World & period saved!',
        errorMessage: 'Failed to save world & period.',
      }
    );
    onRefreshStory();
  };

  const handleActivateStoryVoice = async (voice: SampleStoryVoice) => {
    await executeAsync(
      () => sourceStoriesApi.activateStoryVoice(story.id, voice),
      { successMessage: `${voice.displayName} is now the active story voice!` }
    );
    refreshStoryVoices();
  };

  const handleSaveVoiceInstruction = async () => {
    if (!selectedStoryVoice) return;
    
    await executeAsync(
      () => sourceStoriesApi.activateStoryVoice(story.id, {
        code: selectedStoryVoice.code,
        displayName: selectedStoryVoice.displayName,
        description: voiceInstruction,
        gender: selectedStoryVoice.gender,
        provider: selectedStoryVoice.provider
      }),
      { successMessage: 'Voice instruction updated successfully!' }
    );
    refreshStoryVoices();
  };

  const film = !!hideNarration;
  const tr = (en: string, vi: string) => (film ? vi : en);

  const imageBody = (
        <div style={{ display: 'flex', gap: 24, alignItems: 'flex-start' }}>
          <div style={{ flex: 1 }}>
            <div style={{ marginBottom: 16 }}>
              <Text type="secondary">{tr('Select a visual style for all AI-generated images in this story:', 'Chọn phong cách hình ảnh cho mọi ảnh AI của truyện này.')}</Text>
            </div>
            <Select 
              style={{ width: '100%', maxWidth: 400 }} 
              placeholder={tr('Select image style', 'Chọn phong cách ảnh')} 
              value={story.imageStyle}
              onChange={handleUpdateStyle}
              loading={genLoading || stylesLoading}
              optionLabelProp="label"
            >
              {imageStyles.map(style => (
                <Option key={style.code} value={style.code} label={style.name}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 12, padding: '4px 0' }}>
                    {style.illustrationUrl ? (
                      <img 
                        src={style.illustrationUrl} 
                        alt={style.name} 
                        style={{ width: 40, height: 40, borderRadius: 4, objectFit: 'cover' }} 
                      />
                    ) : (
                      <div style={{ width: 40, height: 40, borderRadius: 4, background: '#f0f0f0', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                        <PictureOutlined style={{ color: '#ccc' }} />
                      </div>
                    )}
                    <Text>{style.name}</Text>
                  </div>
                </Option>
              ))}
            </Select>
            {selectedStyle && (
              <div style={{ marginTop: 16 }}>
                <Text strong>{tr('Description:', 'Mô tả:')}</Text>
                <Paragraph type="secondary" style={{ marginTop: 4 }}>
                  {selectedStyle.description}
                </Paragraph>
              </div>
            )}
          </div>
          {selectedStyle?.illustrationUrl && (
            <div style={{ width: 200 }}>
              <Text strong style={{ display: 'block', marginBottom: 8 }}>{tr('Illustration:', 'Ảnh minh hoạ:')}</Text>
              <img 
                src={selectedStyle.illustrationUrl} 
                alt={selectedStyle.name} 
                style={{ width: '100%', borderRadius: 8, objectFit: 'cover', height: 120, border: '1px solid #f0f0f0' }} 
              />
            </div>
          )}
        </div>
  );

  const worldBody = (
    <>
        {editingWorldSetting ? (
          <WorldSettingEditor
            value={story.worldSetting || null}
            showPin
            // Người đã ngồi sửa nghĩa là đã xem và chốt ⇒ khoá sẵn để AI khỏi đụng vào nữa. Tắt được.
            defaultPinned={story.worldSetting?.source !== 'MANUAL'}
            saving={genLoading}
            onSave={async (next) => { await handleSaveWorldSetting(next); setEditingWorldSetting(false); }}
            onCancel={() => setEditingWorldSetting(false)}
          />
        ) : isEmptyWorldSetting(story.worldSetting) ? (
          <Space direction="vertical" size={8}>
            <Text type="secondary">
              {tr('Not set — AI will fill this in when you create the first Film plan for this story.', 'Chưa đặt — AI sẽ tự điền khi bạn tạo Film plan đầu tiên của truyện này.')}
            </Text>
            <Button size="small" onClick={() => setEditingWorldSetting(true)}>{tr('Set manually (optional)', 'Đặt tay (tuỳ chọn)')}</Button>
          </Space>
        ) : (
          <Space direction="vertical" size={10} style={{ width: '100%' }}>
            <WorldSettingView value={story.worldSetting || null} compact={film} />
            <Space>
              <Button size="small" icon={<EditOutlined />} onClick={() => setEditingWorldSetting(true)}>{tr('Edit', 'Sửa')}</Button>
              {story.worldSetting?.pinned && (
                <Text type="secondary" style={{ fontSize: 12 }}>{tr('📌 Pinned — AI will not overwrite', '📌 Đã khoá — AI sẽ không ghi đè')}</Text>
              )}
            </Space>
          </Space>
        )}
    </>
  );

  const aiCard = (
        <Card 
          title={<span><RobotOutlined style={{ marginRight: 8 }} />AI Overview Analysis</span>}
          extra={
            <Button 
              type="link" 
              size="small" 
              icon={<RobotOutlined />} 
              onClick={handleGeneratePhase1}
              loading={genLoading}
            >
              {overview?.summary ? 'Regenerate Overview' : 'AI Generate Overview'}
            </Button>
          }
          loading={loading}
        >
          {overview?.summary ? (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
              <div>
                <Title level={4}>Summary</Title>
                <Paragraph style={{ whiteSpace: 'pre-wrap' }}>{overview.summary}</Paragraph>
              </div>
              <div>
                <Title level={4} type="success">Pros</Title>
                <Paragraph style={{ whiteSpace: 'pre-wrap' }}>{overview.pros}</Paragraph>
              </div>
              <div>
                <Title level={4} type="danger">Cons</Title>
                <Paragraph style={{ whiteSpace: 'pre-wrap' }}>{overview.cons}</Paragraph>
              </div>
            </div>
          ) : (
            <Empty description="No AI overview yet. Click 'AI Generate Overview' to start." />
          )}
        </Card>
  );

  const marketingCard = (
        <Card 
          title={<span><RobotOutlined style={{ marginRight: 8 }} />Marketing Content</span>}
        >
          <div style={{ display: 'flex', flexDirection: 'column', gap: 24 }}>
            <div>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 8 }}>
                <Title level={4} style={{ margin: 0 }}>Hook</Title>
                <Button 
                  type="link" 
                  size="small" 
                  icon={<RobotOutlined />} 
                  onClick={handleGenerateHook}
                  loading={genLoading}
                  disabled={!overview?.summary}
                >
                  Regenerate Hook
                </Button>
              </div>
              {overview?.hook ? (
                <Paragraph style={{ background: '#f9f9f9', padding: 12, borderRadius: 8, borderLeft: '4px solid #1890ff' }}>
                  {overview.hook}
                </Paragraph>
              ) : (
                <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description="No hook yet." />
              )}
            </div>

            <Divider style={{ margin: 0 }} />

            <div>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 8 }}>
                <Title level={4} style={{ margin: 0 }}>Caption</Title>
                <Button 
                  type="link" 
                  size="small" 
                  icon={<RobotOutlined />} 
                  onClick={handleGenerateCaption}
                  loading={genLoading}
                  disabled={!overview?.summary}
                >
                  Regenerate Caption
                </Button>
              </div>
              {overview?.caption ? (
                <Paragraph style={{ background: '#f9f9f9', padding: 12, borderRadius: 8 }}>
                  {overview.caption}
                </Paragraph>
              ) : (
                <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description="No caption yet." />
              )}
            </div>
          </div>
        </Card>
  );

  const metaParts = [
    story.author ? `Tác giả: ${story.author}` : null,
    `${totalChapters} chương`,
    story.createdAt ? `Tạo ${formatDate(story.createdAt)}` : null,
  ].filter(Boolean) as string[];
  const worldTag = story.worldSetting?.source
    ? <span className={`sp-chip ${story.worldSetting.source === 'AI' ? 'warn' : 'ok'}`}>{story.worldSetting.source === 'AI' ? 'AI suy ra' : 'Thủ công'}</span>
    : null;

  if (film) {
    return (
      <div className="sp-pane">
        <div className="sp-hero" data-testid="story-hero">
          <div className="kick">Truyện nguồn</div>
          <h1>{story.name}</h1>
          <div className="meta">
            {metaParts.join(' · ')}
            {story.link && <>{' · '}<a href={story.link} target="_blank" rel="noopener noreferrer">Link nguồn</a></>}
          </div>
        </div>

        <section className="sp-sec">
          <header className="sp-sec-h"><h2><PictureOutlined /> Phong cách ảnh</h2></header>
          <p className="sp-help">Chỉ tả CHẤT ẢNH (nét vẽ, màu), không nói phim diễn ra thời nào — thời đại nằm ở thẻ «Bối cảnh &amp; thời đại» ngay dưới.</p>
          {imageBody}
        </section>

        {/* Bối cảnh & thời đại (260804) — cạnh Phong cách ảnh vì hai thứ RẤT dễ nhầm là một. */}
        <section className="sp-sec">
          <header className="sp-sec-h"><h2>🏛 Bối cảnh &amp; thời đại</h2>{worldTag}</header>
          <p className="sp-help">Ràng buộc thời đại của mọi ảnh tham chiếu (bối cảnh, trang phục, đạo cụ). AI đọc các chương và tự điền — bạn chỉ cần xem lại và sửa chỗ sai.</p>
          {worldBody}
        </section>

        <details className="sp-more" data-testid="story-more">
          <summary>Khác <span>Phân tích AI · Nội dung quảng bá</span></summary>
          <div className="sp-more-body">
            {aiCard}
            {marketingCard}
          </div>
        </details>

        <StorytellerModal
          visible={isStorytellerModalVisible}
          storyId={story.id}
          storyteller={editingStoryteller}
          onClose={() => setIsStorytellerModalVisible(false)}
          onSuccess={() => {
            setIsStorytellerModalVisible(false);
            refreshStorytellers();
          }}
        />
      </div>
    );
  }

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 24 }}>
      {/* 1. Basic Info */}
      <Card>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
          <div>
            <Title level={2} style={{ marginTop: 0, marginBottom: 16 }}>
              {story.name}
            </Title>
            <Descriptions column={2} size="small">
              <Descriptions.Item label="Author">{story.author || '-'}</Descriptions.Item>
              <Descriptions.Item label="Total Chapters">{totalChapters}</Descriptions.Item>
              <Descriptions.Item label="Link">
                {story.link ? (
                  <a href={story.link} target="_blank" rel="noopener noreferrer">
                    {truncateText(story.link, 50)}
                  </a>
                ) : '-'}
              </Descriptions.Item>
              <Descriptions.Item label="Created At">{formatDate(story.createdAt)}</Descriptions.Item>
            </Descriptions>
          </div>
        </div>
      </Card>

      {/* 1.5 Image Style Selection */}
      <Card title={<span><PictureOutlined style={{ marginRight: 8 }} />Image Style</span>}>
        {imageBody}
      </Card>

      {/* 1.6 Bối cảnh & thời đại (260804) — đặt ngay dưới Image Style vì hai thứ RẤT dễ nhầm là một:
          mã style ("GREEK") chỉ tả CHẤT ẢNH, không nói phim diễn ra thời nào. Để cạnh nhau cho thấy rõ. */}
      <Card
        title={<span>🏛 World & Period</span>}
        extra={
          story.worldSetting?.source && (
            <Tag color={story.worldSetting.source === 'AI' ? 'orange' : 'blue'}>
              {story.worldSetting.source === 'AI' ? 'AI-inferred' : 'Manual'}
            </Tag>
          )
        }
      >
        <div style={{ marginBottom: 12 }}>
          <Text type="secondary">
            Constrains the period of every reference image (locations, costumes, props) in this story.
            {' '}AI reads the chapters and fills this in — just review it and fix what looks wrong.
          </Text>
        </div>

        {worldBody}
      </Card>

      <div style={{ display: 'flex', flexDirection: 'column', gap: 24 }}>
        {/* 2. AI Summary & Pros/Cons */}
        {aiCard}

        {/* 4. Story Voices */}
        {!hideNarration && (
        <Card 
          title={
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <span>Story Voices</span>
              <a 
                href="https://docs.cloud.google.com/text-to-speech/docs/gemini-tts#voice_options" 
                target="_blank" 
                rel="noopener noreferrer"
                style={{ fontSize: '14px' }}
              >
                <Button type="link" icon={<ExportOutlined />} style={{ padding: 0 }}>
                  Explore Voice Samples
                </Button>
              </a>
            </div>
          }
          loading={false}
        >
          <div style={{ maxHeight: '300px', overflowY: 'auto' }}>
            <List
              itemLayout="horizontal"
              dataSource={sampleVoices || []}
              renderItem={(item) => (
                <List.Item
                  style={{ 
                    padding: '12px 16px',
                    borderRadius: 8,
                    marginBottom: 8,
                    border: '1px solid #f0f0f0'
                  }}
                >
                  <List.Item.Meta
                    title={
                      <Space>
                        <Text strong>{item.displayName}</Text>
                        <Tag color={item.gender === 'MALE' ? 'blue' : 'magenta'}>{item.gender}</Tag>
                        {selectedStoryVoice?.code === item.code && selectedStoryVoice?.provider === item.provider ? (
                          <Badge status="processing" text="Active" />
                        ) : (
                          <Button 
                            type="link" 
                            size="small" 
                            style={{ padding: 0, height: 'auto', fontSize: 12 }}
                            onClick={() => handleActivateStoryVoice(item)}
                          >
                            Activate
                          </Button>
                        )}
                      </Space>
                    }
                    description={
                      <div>
                        <div style={{ fontSize: 12 }}>{item.description}</div>
                      </div>
                    }
                  />
                </List.Item>
              )}
              locale={{ emptyText: <Empty description="No voices available." image={Empty.PRESENTED_IMAGE_SIMPLE} /> }}
            />
          </div>
        </Card>
        )}

        {/* 4.5 Voice Instruction (Only if a voice is selected) */}
        {!hideNarration && selectedStoryVoice && (
          <Card 
            title={<span><RobotOutlined style={{ marginRight: 8 }} />Voice Instruction (Style & Tone)</span>}
            extra={
              <Button 
                type="primary" 
                size="small" 
                icon={<SaveOutlined />} 
                onClick={handleSaveVoiceInstruction}
                loading={genLoading}
              >
                Save Instruction
              </Button>
            }
          >
            <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
              <Text type="secondary">
                Describe how the AI should read the story (e.g., "Deep male voice, slow pace, mysterious tone"). 
                This will be applied to all panels in the story.
              </Text>
              <Input.TextArea 
                value={voiceInstruction} 
                onChange={(e) => setVoiceInstruction(e.target.value)}
                placeholder="Enter voice instructions here..."
                autoSize={{ minRows: 3, maxRows: 6 }}
              />
            </div>
          </Card>
        )}
        
        {/* 5. Storyteller Management */}
        {!hideNarration && (
        <Card 
          title="Storytellers" 
          extra={
            <Space>
              <Button 
                type="link" 
                size="small" 
                icon={<RobotOutlined />} 
                onClick={handleGenerateStorytellers}
                loading={genLoading}
                disabled={!overview?.summary}
              >
                AI Suggest
              </Button>
              <Button type="primary" size="small" icon={<PlusOutlined />} onClick={handleAddStoryteller} />
            </Space>
          }
          loading={storytellersLoading}
        >
          <List
            itemLayout="horizontal"
            dataSource={storytellers}
            renderItem={(item) => (
              <List.Item
                actions={[
                  <Tooltip title="Edit">
                    <Button type="text" size="small" icon={<EditOutlined />} onClick={() => handleEditStoryteller(item)} />
                  </Tooltip>,
                  <Popconfirm title="Delete this storyteller?" onConfirm={() => handleDeleteStoryteller(item)}>
                    <Button type="text" danger size="small" icon={<DeleteOutlined />} />
                  </Popconfirm>
                ]}
                style={{ 
                  background: item.isActive ? '#e6f7ff' : 'transparent',
                  padding: '12px 16px',
                  borderRadius: 8,
                  marginBottom: 8,
                  border: item.isActive ? '1px solid #91d5ff' : '1px solid #f0f0f0'
                }}
              >
                <List.Item.Meta
                  title={
                    <Space>
                      <Text strong>{item.name}</Text>
                      {item.isActive ? (
                        <Badge status="processing" text="Active" />
                      ) : (
                        <Button 
                          type="link" 
                          size="small" 
                          style={{ padding: 0, height: 'auto', fontSize: 12 }}
                          onClick={() => handleActivateStoryteller(item)}
                        >
                          Activate
                        </Button>
                      )}
                    </Space>
                  }
                  description={
                    <div>
                      <div style={{ fontSize: 12 }}><Text type="secondary">Personality:</Text> {item.personality}</div>
                      <div style={{ fontSize: 12 }}><Text type="secondary">Style:</Text> {item.style}</div>
                    </div>
                  }
                />
              </List.Item>
            )}
            locale={{ emptyText: <Empty description="No storytellers yet." image={Empty.PRESENTED_IMAGE_SIMPLE} /> }}
          />
        </Card>
        )}

        {/* 3. Hook & Caption */}
        {marketingCard}
      </div>

      <StorytellerModal
        visible={isStorytellerModalVisible}
        storyId={story.id}
        storyteller={editingStoryteller}
        onClose={() => setIsStorytellerModalVisible(false)}
        onSuccess={() => {
          setIsStorytellerModalVisible(false);
          refreshStorytellers();
        }}
      />
    </div>
  );
};

export default OverviewTab;
